import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '@callmart/types';
import MonthPicker from '../UI/MonthPicker';
import { useBudgetWindow } from '../../store/Budget/BudgetContext';
import { scenarioDateErrors } from './scenarioMeta';
import dayjs, { Dayjs } from '@utils/dayjs';

/** Both ends are whole MONTHS, so the range is stored as a first-of-month key like every other month
 *  the Budget module deals with. */
const monthKey = (value: Dayjs | null): string => (value ? value.startOf('month').format(DATE_FORMATS.ISO_DATE) : '');

/**
 * CALL-815: the Overview's `Datum von` / `Datum bis` filter.
 *
 * With no range set the page behaves exactly as before — the 12-month window with its ◀/▶ year
 * navigation. With one set, the grids show every month of the period, however many that is, and the
 * navigation becomes a read-out (MonthWindowNav). The range narrows what is DISPLAYED and totalled; the
 * Excel export is deliberately out of its reach (see BudgetWindowContext).
 *
 * Reads and writes the range straight from context rather than holding a draft of its own: an unusable
 * period (only one end picked, or `bis` before `von`) is simply not applied — isBudgetRangeActive
 * decides that — so there is no second copy of the two dates that could drift out of sync with the view.
 */
const BudgetMonthRangeFilter = () => {
    const { t } = useTranslation('budget');
    const { rangeFrom, rangeTo, setRange } = useBudgetWindow();

    // Only a COMPLETE pair can be wrong here. An empty pair is the default (no range) and half a pair is
    // unfinished — neither is applied (isBudgetRangeActive), so the 12-month window just stays, and marking
    // `bis` "Datum ist erforderlich" the instant `von` is picked accused the user of an error in the middle
    // of making a valid selection. A scenario is different: it MUST have both dates, which is why
    // scenarioDateErrors reports that case and keeps doing so for ScenarioMetaForm.
    //
    // Gating the call, rather than picking two messages out of its result, is what keeps ONE definition of
    // the rules that do apply here: `bis` before `von` and a span past MAX_SCENARIO_MONTHS both need both
    // ends anyway, so with both ends present the helper cannot return `dateRequired` at all — and a pair
    // rule added to it later lands on this filter without anyone having to remember it.
    const errors =
        rangeFrom && rangeTo ? scenarioDateErrors(rangeFrom, rangeTo, t) : { startDate: null, endDate: null };

    /** Emptying EITHER end drops the whole period and restores the 12-month window — half a range shows
     *  nothing, so keeping the other end would only strand the user in the "Datum ist erforderlich" state.
     *  One rule for the × button and for a field cleared from the keyboard alike. */
    const clearRange = () => setRange('', '');
    const setFrom = (value: Dayjs | null) => (value ? setRange(monthKey(value), rangeTo) : clearRange());
    const setTo = (value: Dayjs | null) => (value ? setRange(rangeFrom, monthKey(value)) : clearRange());

    const pickerProps = (error: string | null) => ({
        // CALL-759: the Budget module is German whatever the UI language is, so the calendar and the typed
        // date format must not follow i18n.language the way CustomDatePicker's default does.
        locale: 'de',
        // Without it CustomDatePicker swallows an emptied field, and the range could only be dropped with
        // the mouse.
        allowEmpty: true,
        slotProps: {
            textField: {
                size: 'small' as const,
                error: Boolean(error),
                helperText: error ?? undefined,
                sx: {
                    // The clearable field spends ~110px of its box on padding and the two buttons (× and
                    // calendar) — at 165 that left ~58px of text, which is what cut "August 2026" off at
                    // "August 2". "September 2026" is the longest German label at ~105px, so 240 keeps it
                    // whole with room to spare. minWidth, not width, so the wrapping filter bar can never
                    // squeeze the truncation back in.
                    minWidth: 240,
                    // The message is painted in the gap BELOW the bar instead of taking a row of its own,
                    // so the bar is exactly as tall with an error as without one. In flow it grew the
                    // field's box by ~23px (20px of text + MUI's 3px marginTop) and pushed the checkboxes
                    // beside it — and the heading under it — down the moment a reversed pair was typed.
                    //
                    // Absolute against the FIELD (MUI's FormControl root is position: relative), not
                    // against the bar, so it follows its own field to wherever the bar wraps it; the bar's
                    // rowGap is set to clear it there. Reserving the space instead would have been the
                    // other way to a constant height, but it costs that ~23px band under the dates for
                    // good, and it re-centres every checkbox in the bar 11px below the fields they sit
                    // next to — a permanent misalignment to accommodate a transient message.
                    '& .MuiFormHelperText-root': {
                        position: 'absolute',
                        top: '100%',
                        // The German messages run past 240px; wrapping them would take two lines and
                        // outgrow the gap they are painted in. Nothing sits beside them to collide with.
                        whiteSpace: 'nowrap',
                    },
                },
            },
            field: { clearable: true, onClear: clearRange },
        },
    });

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonthPicker
                label={t('filter.dateFrom')}
                date={rangeFrom ? dayjs(rangeFrom, DATE_FORMATS.ISO_DATE) : null}
                onDateChange={setFrom}
                {...pickerProps(errors.startDate)}
            />
            <MonthPicker
                label={t('filter.dateTo')}
                date={rangeTo ? dayjs(rangeTo, DATE_FORMATS.ISO_DATE) : null}
                onDateChange={setTo}
                {...pickerProps(errors.endDate)}
            />
        </Box>
    );
};

export default BudgetMonthRangeFilter;
